import React from 'react';
import { StandardPdfTemplate } from '../../templates/StandardPdfTemplate';
import { ProjectData, ProjectInfoData } from '../../shared/types/PdfCommonTypes';
import { TFunction } from 'i18next';
import { UnpavedResultsCore } from './sections/UnpavedResultsCore';

interface UnpavedResultsPdfDocumentProps {
    output: any;
    projectInfo: any;
    dynamicProject: any;
    tireImageUnreinforced: string;
    tireImageReinforced: string;
    tireImageGeotextile?: string;
    chartImage: string;
    t: TFunction;
}

const UnpavedResultsPdfDocumentV2: React.FC<UnpavedResultsPdfDocumentProps> = ({
    output,
    projectInfo,
    dynamicProject,
    tireImageUnreinforced,
    tireImageReinforced,
    tireImageGeotextile,
    chartImage,
    t,
}) => {
    // Extract project information
    const projectData = dynamicProject?.data;
    const projectInfoData = projectData?.projectInfo;
    const projectMapImage = projectData?.mapImage;
    const unitSystem = dynamicProject?.data?.unit || 'Metric';
    const projectType = 'unpaved';
    const calculationMethodKey = projectData?.method || projectInfo?.method || projectInfoData?.data?.method;
    const calculationMethodLabels: Record<string, string> = {
        giroud: 'Giroud-Han',
        leng: 'Leng-Gabr',
    };
    const calculationMethodLabel = calculationMethodKey ? calculationMethodLabels[calculationMethodKey] || calculationMethodKey : undefined;
    const subtitleDesigning = t('subtitle_designing') || 'Designing';
    const subtitleWithMethod = calculationMethodLabel ? `${subtitleDesigning} ${calculationMethodLabel}` : subtitleDesigning;
    const projectInfoDescriptionKey = calculationMethodKey === 'leng' ? 'unpaved_leng_project_info_desc_pdf' : 'unpaved_giroud_project_info_desc_pdf';
    const projectInfoDescription = t(projectInfoDescriptionKey) || 'Project Information';

    // Prepare data for StandardPdfTemplate
    const projectDataForTemplate: ProjectData = {
        projectName: projectData?.projectName || 'N/A',
        projectType: projectType,
        unit: unitSystem,
        mapImage: projectMapImage,
    };

    const projectInfoDataForTemplate: ProjectInfoData = {
        data: {
            nrProject: projectInfo?.nrProject || projectInfoData?.data?.nrProject || 'N/A',
        },
        projectOwner: projectInfo?.projectOwner || projectInfoData?.projectOwner || 'N/A',
        designerFirmName: projectInfo?.designerFirmName || projectInfoData?.designerFirmName || 'N/A',
        designerFirmCountry: projectInfo?.designerFirmCountry || projectInfoData?.designerFirmCountry || 'N/A',
        designerFirmCity: projectInfo?.designerFirmCity || projectInfoData?.data?.designerFirmCity || 'N/A',
        coordinates: projectInfo?.coordinates || projectInfoData?.coordinates || [null, null],
    };

    return (
        <StandardPdfTemplate
            projectType={projectType}
            projectData={projectDataForTemplate}
            projectInfoData={projectInfoDataForTemplate}
            t={t}
            headerConfig={{
                customTitle: t('pdf_header_technical_report_groundmac') || 'GROUNDMAC - Design Technical Report',
                customProjectType: t('project_type_road_unpaved') || 'Road Unpaved',
                customSubtitle: subtitleWithMethod,
                showGeogridSubtitle: false,
            }}
            footerConfig={{
                customText: t('pdf_footer') || 'MACread eDesign',
                customNotice: t('pdf_footer_notice') || 'Footer notice',
            }}
            showProjectInfo={true}>
            {/* Use the shared core component */}
            <UnpavedResultsCore
                output={output}
                projectData={projectData}
                projectInfo={projectInfo}
                tireImageUnreinforced={tireImageUnreinforced}
                tireImageReinforced={tireImageReinforced}
                tireImageGeotextile={tireImageGeotextile}
                chartImage={chartImage}
                t={t}
                projectInfoDescription={projectInfoDescription}
                showInputData={true}
                showChart={true}
                showResults={true}
            />
        </StandardPdfTemplate>
    );
};

export default UnpavedResultsPdfDocumentV2;
